import { __rest, __assign } from '../../node_modules/tslib/tslib.es6.js';
import React from 'react';
import { useTheme, Button, Spinner, themeGet } from '@primer/react';
import { ColorSchemes } from '../../utils/constants.js';

// Enums for Size, Variant, Type, and LoadingPosition
var LTButtonSize;
(function (LTButtonSize) {
  LTButtonSize["LARGE"] = "large";
  LTButtonSize["MEDIUM"] = "medium";
  LTButtonSize["SMALL"] = "small";
})(LTButtonSize || (LTButtonSize = {}));
var LTButtonVariant;
(function (LTButtonVariant) {
  LTButtonVariant["DANGER"] = "danger";
  LTButtonVariant["DEFAULT"] = "default";
  LTButtonVariant["INVISIBLE"] = "invisible";
  LTButtonVariant["OUTLINE"] = "outline";
  LTButtonVariant["PRIMARY"] = "primary";
})(LTButtonVariant || (LTButtonVariant = {}));
var LTButtonType;
(function (LTButtonType) {
  LTButtonType["DEFAULT"] = "default";
  LTButtonType["FLOATING"] = "floating";
})(LTButtonType || (LTButtonType = {}));
var LTButtonLoadingPosition;
(function (LTButtonLoadingPosition) {
  LTButtonLoadingPosition["LEADING"] = "leading";
  LTButtonLoadingPosition["TRAILING"] = "trailing";
})(LTButtonLoadingPosition || (LTButtonLoadingPosition = {}));
var LTButton = function LTButton(_a) {
  var _b, _c, _d, _e, _f;
  var _g = _a.ariaLabel,
    ariaLabel = _g === void 0 ? undefined : _g,
    _h = _a.block,
    block = _h === void 0 ? false : _h,
    _j = _a.children,
    children = _j === void 0 ? null : _j,
    _k = _a.className,
    className = _k === void 0 ? "" : _k,
    _l = _a.disabled,
    disabled = _l === void 0 ? false : _l,
    _m = _a.forwardRef,
    forwardRef = _m === void 0 ? null : _m,
    _o = _a.id,
    id = _o === void 0 ? "" : _o,
    _p = _a.label,
    label = _p === void 0 ? "" : _p,
    _q = _a.loaderPosition,
    loaderPosition = _q === void 0 ? LTButton.LoadingPosition.LEADING : _q,
    _r = _a.loading,
    loading = _r === void 0 ? false : _r,
    _s = _a.leadingIcon,
    leadingIcon = _s === void 0 ? null : _s,
    _t = _a.onClick,
    onClick = _t === void 0 ? function () {} : _t,
    _u = _a.size,
    size = _u === void 0 ? LTButton.Size.SMALL : _u,
    _v = _a.style,
    style = _v === void 0 ? {} : _v,
    _w = _a.trailingIcon,
    trailingIcon = _w === void 0 ? null : _w,
    _x = _a.type,
    type = _x === void 0 ? LTButton.Type.DEFAULT : _x,
    _y = _a.variant,
    variant = _y === void 0 ? LTButton.Variant.OUTLINE : _y,
    rest = __rest(_a, ["ariaLabel", "block", "children", "className", "disabled", "forwardRef", "id", "label", "loaderPosition", "loading", "leadingIcon", "onClick", "size", "style", "trailingIcon", "type", "variant"]);
  var colorScheme = useTheme().colorScheme;
  var ActiveBackgroundColorStyle = (_b = {}, _b[LTButton.Variant.DANGER] = "#C11F2A", _b[LTButton.Variant.DEFAULT] = "#eee", _b[LTButton.Variant.INVISIBLE] = "#ddd", _b[LTButton.Variant.OUTLINE] = "#eee", _b[LTButton.Variant.PRIMARY] = "#333333", _b);
  var ActiveBoxShadowStyle = (_c = {}, _c[LTButton.Variant.DANGER] = "0px 1px 0px 0px rgba(134, 24, 29, 0.20) inset;", _c[LTButton.Variant.DEFAULT] = "0px 2.4px 0.3px 0px rgba(17, 17, 17, 0.15) inset;", _c[LTButton.Variant.INVISIBLE] = "none", _c[LTButton.Variant.OUTLINE] = "#eee", _c[LTButton.Variant.PRIMARY] = "0px 2px 0px 0px #00000080 inset;", _c);
  var disabledTextColor = (_d = {}, _d[LTButton.Variant.DANGER] = "#CF222E", _d[LTButton.Variant.DEFAULT] = "#888888", _d[LTButton.Variant.INVISIBLE] = "#888888", _d[LTButton.Variant.OUTLINE] = "#888888", _d[LTButton.Variant.PRIMARY] = "#FFFFFF", _d);
  var DefaultTextColor = (_e = {},
  // Default value is picked from "accent.fg".
  _e[LTButton.Variant.INVISIBLE] = "#101010", _e);
  var BorderColorStyle = (_f = {}, _f[LTButton.Variant.DEFAULT] = "border.default", _f[LTButton.Variant.INVISIBLE] = "border.invisibleBtn", _f[LTButton.Variant.PRIMARY] = disabled ? "border.primaryBtnDisabled" : "border.primaryBtn", _f);
  var ButtonStyle = __assign({
    span: {
      display: "flex",
      alignItems: "center"
    },
    "svg:last-child": {
      marginLeft: "4px"
    },
    "svg:first-child": {
      marginRight: "4px"
    }
  }, colorScheme !== ColorSchemes.DARK && {
    // light and high_contrast styles
    // TODO: Move these styles to theme to make it consistent for all buttons across componenets.
    borderColor: BorderColorStyle[variant] || "",
    "&:active:not([disabled])": {
      boxShadow: ActiveBoxShadowStyle[variant],
      backgroundColor: ActiveBackgroundColorStyle[variant]
    },
    "&:disabled": {
      color: disabledTextColor[variant]
    },
    color: DefaultTextColor[variant] || ""
  });
  var FloatingButtonStyle = {
    alignItems: "center",
    borderRadius: "26px",
    bottom: "24px",
    boxShadow: "0px 48px 96px -24px rgba(66, 74, 83, 0.08), 0px 24px 48px -12px rgba(66, 74, 83, 0.08), 0px 4px 32px -4px rgba(66, 74, 83, 0.08), 0px 8px 16px -4px rgba(66, 74, 83, 0.08);",
    display: "flex",
    gap: "4px",
    position: "absolute",
    right: "24px",
    width: "max-content"
  };
  var handleClick = function handleClick(event) {
    if (!loading) {
      onClick(event);
    }
  };
  return /*#__PURE__*/React.createElement(Button, __assign({
    "aria-label": ariaLabel,
    "aria-disabled": disabled,
    block: block,
    className: className,
    disabled: disabled,
    id: id,
    loading: loading,
    onClick: handleClick,
    ref: forwardRef,
    sx: __assign(__assign(__assign({}, ButtonStyle), style), type === LTButton.Type.DEFAULT ? {} : FloatingButtonStyle),
    size: size,
    variant: variant
  }, rest), !!loading && loaderPosition === LTButton.LoadingPosition.LEADING && /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement(Spinner, {
    sx: {
      marginRight: themeGet("space.2")
    },
    size: "small"
  })), !!leadingIcon && /*#__PURE__*/React.createElement("span", {
    style: {
      marginRight: "4px"
    }
  }, leadingIcon), label ? label : children, !!trailingIcon && trailingIcon, !!loading && loaderPosition === LTButton.LoadingPosition.TRAILING && /*#__PURE__*/React.createElement(Spinner, {
    size: "small"
  }));
};
// Attach enums to the component for external use
LTButton.Size = LTButtonSize;
LTButton.Variant = LTButtonVariant;
LTButton.Type = LTButtonType;
LTButton.LoadingPosition = LTButtonLoadingPosition;

export { LTButton as default };
